import api from 'api/instance';
import { Status } from 'types/items';

import { Location, WashhubInventory } from 'types/washhub';
import { toMidnightISOString } from 'utils/dateUtils';

const URL = `${process.env.REACT_APP_API_URL}`;

export const fetchLocations = async (): Promise<Location[]> => {
  const { data } = await api.get(`${URL}/location/list`);

  return data?.items;
};

export const fetchLocationById = async (id: string): Promise<Location> => {
  const { data } = await api.get(`${URL}/location/${id}`);

  return data?.item;
};

export const createLocation = async (payload: Location) => {
  const { data } = await api.post(`${URL}/location/create`, payload);

  return data;
};

export const uploadInventoryItem = async (
  payload: {
    file: File;
    sku: string;
    skuCode: string;
    washHub: string;
    washHubCode: string;
  },
  type: string,
) => {
  // temp size property. will be removed once api is updated
  const size = 16;

  if (type === 'item') {
    payload = {
      ...payload,
      size,
    } as any;
  }

  const formData = toFormData(payload);

  const { data } = await api.post(`${URL}/${type}/upload-${type}s`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return data;
};

export const fetchInventoryItems = async (washHubId: string) => {
  const { data } = await api.get(`${URL}/item/list`, {
    params: {
      washHub: washHubId,
    },
  });

  return data?.items;
};

export function toFormData(obj: any, form?: any, namespace?: any) {
  let formKey;

  const fd = form || new FormData();

  for (const property in obj) {
    const value = obj[property];

    if (namespace) {
      formKey = namespace + '[' + property + ']';
    } else {
      formKey = property;
    }
    if (value instanceof Date) {
      fd.append(formKey, value.toISOString());
    } else if (typeof value === 'object' && !(value instanceof File)) {
      toFormData(value, fd, formKey);
    } else {
      fd.append(formKey, value);
    }
  }

  return fd;
}

export const fetchWashHubs = async (): Promise<Location[]> => {
  const { data } = await api.get(`${URL}/location/get-permitted-washhub`);

  return data?.items;
};

export const deleteWashHubs = async (ids: string[]) => {
  const { data } = await api.delete(`${URL}/location/remove-many`, {
    data: { ids },
  });

  return data;
};

type SkuItem = { sku: string; quantity: number };

export const handleTransfer = async (payload: {
  items: Array<SkuItem>;
  cases: Array<SkuItem>;
  packages: Array<SkuItem>;
  equipment: Array<SkuItem>;
  operation: Status.TRANSFER_OUT | Status.TRANSFER_IN;
  washHub: string;
}) => {
  const { data } = await api.post(`${URL}/transfer/initiate-transfer`, payload);

  return data;
};

export const fetchTransferList = async (params: {
  washHub: string;
  page?: number;
  limit?: number;
  startDate?: Date;
  endDate?: Date;
}) => {
  const endDate = toMidnightISOString(params.endDate);
  const startDate = toMidnightISOString(params.startDate);

  const { data } = await api.get(`${URL}/transfer/list`, {
    params: {
      ...params,
      endDate,
      startDate,
    },
  });

  return data?.items as any[];
};

export const fetchWashHubInventoryCountList = async (params: {
  washHub: string;
  page?: number;
  limit?: number;
}) => {
  const { data } = await api.get(`${URL}/washhub-inventory-count/list`, {
    params: {
      ...params,
    },
  });

  return data;
};

export const addHubInventoryCount = async (payload: WashhubInventory) => {
  const { data } = await api.post(`${URL}/washhub-inventory-count`, payload);

  return data;
};

export const editHubInventoryCount = async (
  payload: WashhubInventory & { _id: string },
) => {
  const { _id, ..._payload } = payload;

  const { data } = await api.patch(
    `${URL}/washhub-inventory-count/${_id}`,
    _payload,
  );

  return data;
};

export const deleteHubInventoryCount = async (_id: string) => {
  const { data } = await api.delete(`${URL}/washhub-inventory-count/${_id}`);

  return data;
};
export const exportHubInventoryCount = async (payload: { washHub: string }) => {
  const { data } = await api.post<any>(
    `${URL}/washhub-inventory-count/export`,
    payload,
  );

  return data;
};
